import React, { useCallback, useEffect, useRef, useState } from 'react';
import APIService from '../../services/api';
import { confirmErpia, alertErpia } from '../ConfirmDialog';

/**
 * Le module « Assistant du site » — dans SA page du Marketplace (12.10 :
 * « un endpoint d'ERPIA appelé par l'assistant IA du site pour la formation,
 * un petit éditeur de prompt, et une URL à mettre dans la page Carrières »).
 *
 * Ce que l'administrateur fait ici :
 *  - copie LA LIGNE à coller dans son site (le widget hébergé : bulle et
 *    fenêtre, à sa couleur, servies par le serveur — 13.10, « demain un
 *    autre client crée son chatbot et l'intègre dans son site ») ; ou, pour
 *    un site qui a DÉJÀ son widget (la page Carrières), l'adresse et
 *    l'extrait qui branchent sa fonction (message, history) → reply ;
 *  - dit QUELS SITES ont le droit d'appeler (origines) — sans site, pas de
 *    mise en ligne : le serveur refuse et le dit sous le champ ;
 *  - choisit la COULEUR de la bulle ;
 *  - écrit ce que l'assistant SAIT (informations) et comment il parle
 *    (consignes), l'accueil, les suggestions, le texte de repli ;
 *  - l'ESSAIE, ici, avant de le mettre en ligne ;
 *  - lit ce que les visiteurs demandent, pour compléter ce qui manque ;
 *  - renouvelle la clé (l'ancienne adresse meurt) ou supprime l'assistant.
 *
 * Routes : GET/POST /assistant-site · GET/PUT/DELETE /assistant-site/:id ·
 * POST /assistant-site/:id/cle · POST /assistant-site/:id/essai ·
 * GET /assistant-site/:id/journal. Toutes réservées aux administrateurs ;
 * la route publique, elle, est celle de l'adresse affichée.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const quand = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'; };

async function appel(url, options = {}) {
  const r = await fetch(url, { ...options, headers: { ...authH(), ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(options.headers || {}) } });
  if (r.status === 404 && !options.body && !/\/assistant-site\/\d/.test(url)) return { absent: true };
  if (r.status === 501) return { absent: true };
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) return { error: j.error || `Le serveur a répondu ${r.status}.`, champ: j.champ || null, status: r.status, ...j };
  return j;
}

// Les champs de l'éditeur, dans l'ordre où on les lit. `lignes` : une liste
// saisie une entrée par ligne (le serveur accepte les deux formes).
const CHAMPS_LISTE = ['suggestions', 'origines'];
const ISSUES = {
  ok: null,
  refus: 'L\'IA a refusé de répondre à ce message : le texte de repli a été envoyé.',
  indisponible: 'L\'IA n\'a pas répondu : le texte de repli a été envoyé.',
  plafond: 'Plafond du jour atteint : le texte de repli a été envoyé.',
  cadence: 'Trop de messages d\'un coup : le visiteur a été invité à patienter.',
  invalide: 'Message refusé (vide ou trop long).',
};

export default function AssistantSiteModule({ showNotification }) {
  const [assistants, setAssistants] = useState(null);
  const [courant, setCourant] = useState(null);     // id de l'assistant ouvert
  const [form, setForm] = useState(null);           // copie éditable
  const [refus, setRefus] = useState(null);         // { champ, message }
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [journal, setJournal] = useState(null);
  const [chat, setChat] = useState([]);             // essai : [{ role, text, issue }]
  const [question, setQuestion] = useState('');
  const chatRef = useRef(null);

  const assistant = (assistants || []).find(a => Number(a.id) === Number(courant)) || null;

  const charger = useCallback(async () => {
    const j = await appel(`${BASE}/assistant-site`);
    if (j.absent) { setAbsent(true); return; }
    if (j.error) { setErreur(j.error); return; }
    setAssistants(j.assistants || []);
    setCourant(c => (c && (j.assistants || []).some(a => Number(a.id) === Number(c)) ? c : (j.assistants?.[0]?.id ?? null)));
  }, []);
  useEffect(() => { charger().catch(() => setErreur('Le serveur ne répond pas — réessayez dans un instant.')); }, [charger]);

  // Le formulaire suit l'assistant ouvert ; le journal se relit avec lui.
  useEffect(() => {
    if (!assistant) { setForm(null); setJournal(null); return; }
    setForm({
      nom: assistant.nom || '', accueil: assistant.accueil || '', consignes: assistant.consignes || '', connaissances: assistant.connaissances || '',
      lien_contact: assistant.lien_contact || '', repli: assistant.repli || '', plafond_jour: assistant.plafond_jour ?? 300,
      suggestions: (assistant.suggestions || []).join('\n'), origines: (assistant.origines || []).join('\n'),
      couleur: assistant.couleur || '',
    });
    setRefus(null); setChat([]);
    appel(`${BASE}/assistant-site/${assistant.id}/journal?limit=40`).then(j => setJournal(j.error ? [] : (j.echanges || []))).catch(() => setJournal([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant?.id, assistant?.updated_at]);

  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [chat]);

  const remplacer = (a) => setAssistants(liste => (liste || []).map(x => (Number(x.id) === Number(a.id) ? a : x)));
  const champ = (k) => ({
    value: form?.[k] ?? '',
    onChange: e => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); if (refus?.champ === k) setRefus(null); },
    'aria-invalid': refus?.champ === k || undefined,
    disabled: !!occupe,
  });
  const corps = () => Object.fromEntries(Object.entries(form).map(([k, v]) => [k, CHAMPS_LISTE.includes(k) ? String(v).split('\n').map(s => s.trim()).filter(Boolean) : v]));

  const creer = async () => {
    setOccupe('creer'); setErreur(null);
    try {
      const j = await appel(`${BASE}/assistant-site`, { method: 'POST', body: JSON.stringify({ nom: 'Assistant du site' }) });
      if (j.absent) { setAbsent(true); return; }
      if (j.error) { setErreur(j.error); return; }
      setAssistants(liste => [...(liste || []), j.assistant]); setCourant(j.assistant.id);
      showNotification?.('Assistant créé — écrivez ce qu\'il sait, puis collez l\'adresse dans votre site.', 'success');
    } catch { setErreur('Le serveur ne répond pas — réessayez dans un instant.'); }
    finally { setOccupe(null); }
  };
  const enregistrer = async () => {
    if (!assistant || !form) return;
    setOccupe('enregistrer'); setRefus(null); setErreur(null);
    try {
      const j = await appel(`${BASE}/assistant-site/${assistant.id}`, { method: 'PUT', body: JSON.stringify(corps()) });
      if (j.error) { if (j.champ) setRefus({ champ: j.champ, message: j.error }); else setErreur(j.error); return; }
      remplacer(j.assistant);
      showNotification?.('Réglages enregistrés — l\'assistant répond avec, dès maintenant.', 'success');
    } catch { setErreur('Le serveur ne répond pas — réessayez dans un instant.'); }
    finally { setOccupe(null); }
  };
  const basculer = async () => {
    if (!assistant) return;
    setOccupe('actif');
    try {
      const j = await appel(`${BASE}/assistant-site/${assistant.id}`, { method: 'PUT', body: JSON.stringify({ actif: !assistant.actif }) });
      // « Mettre en ligne » sans site autorisé : le serveur refuse en nommant
      // le champ — le refus s'affiche sous « Sites autorisés », là où ça se corrige.
      if (j.error) { if (j.champ) setRefus({ champ: j.champ, message: j.error }); else setErreur(j.error); return; }
      remplacer(j.assistant);
      showNotification?.(j.assistant.actif ? 'Assistant en ligne : le site reçoit des réponses.' : 'Assistant hors ligne : le site reçoit le texte de repli.', 'success');
    } finally { setOccupe(null); }
  };
  const copier = async (libelle, valeur) => {
    try { await navigator.clipboard.writeText(valeur); showNotification?.(`${libelle} copié.`, 'success'); }
    catch { await alertErpia({ title: `Copier ${libelle.toLowerCase()}`, message: 'Le presse-papiers est indisponible. Sélectionnez puis copiez la valeur ci-dessous.', detail: valeur }); }
  };
  const renouveler = async () => {
    if (!assistant) return;
    const ok = await confirmErpia({ title: 'Renouveler la clé', tone: 'danger', confirmLabel: 'Renouveler',
      message: 'L\'adresse actuelle cessera de répondre immédiatement : le site affichera le texte de repli jusqu\'à ce que vous y colliez la nouvelle adresse. Continuer ?' });
    if (!ok) return;
    setOccupe('cle');
    try {
      const j = await appel(`${BASE}/assistant-site/${assistant.id}/cle`, { method: 'POST', body: '{}' });
      if (j.error) { setErreur(j.error); return; }
      remplacer(j.assistant);
      showNotification?.('Clé renouvelée — collez la nouvelle adresse dans votre site.', 'success');
    } finally { setOccupe(null); }
  };
  const supprimer = async () => {
    if (!assistant) return;
    const ok = await confirmErpia({ title: 'Supprimer cet assistant', tone: 'danger', confirmLabel: 'Supprimer',
      message: `« ${assistant.nom} » et son journal seront supprimés ; le site ne recevra plus de réponses. Continuer ?` });
    if (!ok) return;
    setOccupe('supprimer');
    try {
      const j = await appel(`${BASE}/assistant-site/${assistant.id}`, { method: 'DELETE' });
      if (j.error) { setErreur(j.error); return; }
      setAssistants(liste => (liste || []).filter(x => Number(x.id) !== Number(assistant.id))); setCourant(null);
      showNotification?.('Assistant supprimé.', 'success');
    } finally { setOccupe(null); }
  };
  // L'essai : le même moteur que le site, avec l'historique dans la forme du
  // widget — ce qu'on voit ici est ce que le visiteur verra.
  const essayer = async (texte) => {
    const message = String(texte || question).trim();
    if (!assistant || !message || occupe) return;
    const history = chat.map(m => ({ role: m.role, text: m.text, time: Date.now() }));
    setChat(c => [...c, { role: 'user', text: message }]); setQuestion(''); setOccupe('essai');
    try {
      const j = await appel(`${BASE}/assistant-site/${assistant.id}/essai`, { method: 'POST', body: JSON.stringify({ message, history }) });
      setChat(c => [...c, { role: 'bot', text: j.reply || j.error || 'Pas de réponse.', issue: j.issue || (j.error ? 'invalide' : 'ok') }]);
      appel(`${BASE}/assistant-site/${assistant.id}/journal?limit=40`).then(x => { if (!x.error) setJournal(x.echanges || []); }).catch(() => {});
    } catch { setChat(c => [...c, { role: 'bot', text: 'Le serveur ne répond pas — réessayez dans un instant.', issue: 'indisponible' }]); }
    finally { setOccupe(null); }
  };

  if (absent) return <div className="as-mod erp-card"><p className="as-mod__muet">Le module Assistant du site arrive côté serveur : rien n'est encore branché.</p></div>;
  if (!assistants) return <div className="as-mod erp-card"><p className="as-mod__muet"><BI icon="arrow-repeat" className="as-mod__tourne" /> Chargement…</p>{erreur && <p className="as-mod__erreur" role="alert">{erreur}</p>}</div>;

  if (!assistants.length) {
    return (
      <div className="as-mod">
        {erreur && <p className="as-mod__erreur" role="alert">{erreur}</p>}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre">Votre assistant</h3>
          <p className="as-mod__texte">Le chatbot de votre site (page Carrières, formation) répondra avec <strong>ce que vous écrivez ici</strong> — vos consignes et vos informations — et renverra vers votre formulaire de contact dès qu'il ne sait pas. Il n'a accès à rien d'autre dans ERPIA.</p>
          <p className="as-mod__muet">Trois étapes : créer l'assistant, écrire ce qu'il sait, coller l'adresse dans le code de votre site.</p>
          <div className="as-mod__actions">
            <button type="button" className="erp-btn erp-btn--primary" onClick={creer} disabled={!!occupe}><BI icon={occupe ? 'arrow-repeat' : 'plus-lg'} className={occupe ? 'as-mod__tourne' : undefined} /> Créer l'assistant</button>
          </div>
        </section>
      </div>
    );
  }

  const stats = assistant?.stats || {};
  const sansInfos = !String(form?.connaissances || '').trim();
  const sansOrigines = !String(form?.origines || '').trim();

  return (
    <div className="as-mod">
      {erreur && <p className="as-mod__erreur" role="alert">{erreur}</p>}

      {/* Un onglet par assistant — un second sert à une autre page, un autre site. */}
      <div className="as-mod__onglets" role="tablist" aria-label="Assistants">
          {assistants.map(a => (
            <button key={a.id} type="button" role="tab" aria-selected={Number(a.id) === Number(courant)}
              className={`as-mod__onglet${Number(a.id) === Number(courant) ? ' is-actif' : ''}${a.actif ? '' : ' is-eteint'}`} onClick={() => setCourant(a.id)}>
              {a.nom}{a.actif ? '' : ' · hors ligne'}
            </button>
          ))}
          <button type="button" className="as-mod__onglet" onClick={creer} disabled={!!occupe} title="Un second assistant, pour une autre page ou un autre site"><BI icon="plus-lg" /> Ajouter</button>
      </div>

      {assistant && form && (<>
        {/* ── 1. Dans la page du site ── */}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre"><BI icon="code-slash" /> Dans votre site</h3>
          {!assistant.actif && (sansOrigines
            ? <p className="as-mod__alerte">Cet assistant est <strong>hors ligne</strong> : indiquez votre site dans « Sites autorisés », enregistrez, puis mettez-le en ligne. Tant qu'il est hors ligne, aucune bulle ne s'affiche sur le site.</p>
            : <p className="as-mod__alerte">Cet assistant est <strong>hors ligne</strong> : aucune bulle ne s'affiche sur le site (un widget déjà en place reçoit le texte de repli). Remettez-le en ligne ci-dessous quand il est prêt.</p>)}
          {assistant.actif && sansOrigines && <p className="as-mod__alerte">En ligne <strong>sans site autorisé</strong> : n'importe quel site qui connaît l'adresse peut l'appeler. Indiquez le vôtre ci-dessous et enregistrez.</p>}
          {sansInfos && <p className="as-mod__alerte">Aucune information n'est renseignée : l'assistant dira qu'il ne peut pas répondre. Écrivez ce qu'il sait dans « Ce que l'assistant sait », plus bas.</p>}
          {assistant.widget && (
            <div className="as-mod__champ">
              <span>La ligne à coller dans votre site <small>avant <code>{'</body>'}</code>, sur chaque page où la bulle doit apparaître</small></span>
              <pre className="as-mod__code as-mod__code--ligne">{assistant.widget}<button type="button" className="erp-btn erp-btn--secondary erp-btn--sm as-mod__copier" onClick={() => copier('La ligne', assistant.widget)}><BI icon="clipboard" /> Copier</button></pre>
              <p className="as-mod__aide">Elle dessine la bulle et la fenêtre de conversation, à votre couleur, avec votre accueil et vos suggestions, et parle à l'assistant. Rien d'autre à installer.</p>
            </div>
          )}
          <div className="as-mod__grille">
            <label className="as-mod__champ">
              <span>Couleur de la bulle <small>vide = la couleur par défaut</small></span>
              <div className="as-mod__couleur">
                {/* La couleur par défaut vient du serveur : l'écran n'en écrit aucune. */}
                {assistant.couleur_defaut && <input type="color" aria-label="Choisir la couleur" value={/^#[0-9a-f]{6}$/i.test(form.couleur || '') ? form.couleur : assistant.couleur_defaut} disabled={!!occupe}
                  onChange={e => { const v = e.target.value; setForm(f => ({ ...f, couleur: v })); if (refus?.champ === 'couleur') setRefus(null); }} />}
                <input className="erp-field" placeholder={assistant.couleur_defaut || ''} maxLength={7} {...champ('couleur')} />
              </div>
              {refus?.champ === 'couleur' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}
            </label>
            <div className="as-mod__champ">
              <span>Sites autorisés à appeler cet assistant <small>une origine par ligne</small></span>
              <textarea className="erp-field" rows={2} placeholder={'https://www.votre-site.ch\nhttps://votre-site.ch'} {...champ('origines')} />
              {refus?.champ === 'origines' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}
              <p className="as-mod__aide">{sansOrigines
                ? 'Le schéma et le domaine, sans chemin. Un sous-domaine (www.) est une origine à part — listez les deux si besoin. Un site autorisé est nécessaire pour mettre l\'assistant en ligne.'
                : 'Seuls ces sites reçoivent des réponses ; un autre est refusé. Un sous-domaine (www.) est une origine à part — listez les deux si besoin.'}</p>
            </div>
          </div>
          <details className="as-mod__details">
            <summary>Votre site a déjà un widget de chat ? L'adresse et l'extrait pour le brancher</summary>
            <div>
              <div className="as-mod__champ">
                <span>L'adresse que le site appelle</span>
                <pre className="as-mod__code as-mod__code--ligne">{assistant.url}<button type="button" className="erp-btn erp-btn--secondary erp-btn--sm as-mod__copier" onClick={() => copier('L\'adresse', assistant.url)}><BI icon="clipboard" /> Copier</button></pre>
                <p className="as-mod__aide">Le site envoie <code>{'{ message, history }'}</code> et reçoit <code>{'{ reply }'}</code>. Si l'assistant ne peut pas répondre (hors ligne, plafond atteint), <code>reply</code> porte le texte de repli : le visiteur voit toujours une phrase.</p>
              </div>
              <div className="as-mod__champ">
                <span>L'extrait à coller dans la page (après le script du chatbot)</span>
                <pre className="as-mod__code">{assistant.extrait}<button type="button" className="erp-btn erp-btn--secondary erp-btn--sm as-mod__copier" onClick={() => copier('L\'extrait', assistant.extrait)}><BI icon="clipboard" /> Copier</button></pre>
              </div>
            </div>
          </details>
          <div className="as-mod__actions">
            <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={!!occupe}><BI icon="check-lg" /> Enregistrer</button>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={basculer} disabled={!!occupe}><BI icon={assistant.actif ? 'pause-circle' : 'play-circle'} /> {assistant.actif ? 'Mettre hors ligne' : 'Mettre en ligne'}</button>
          </div>
        </section>

        {/* ── 2. Ce que l'assistant dit ── */}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre"><BI icon="chat-left-text" /> Ce que l'assistant sait, et comment il parle</h3>
          <div className="as-mod__grille">
            <label className="as-mod__champ"><span>Nom de l'assistant</span><input className="erp-field" {...champ('nom')} />{refus?.champ === 'nom' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
            <label className="as-mod__champ"><span>Lien du formulaire de contact <small>où il renvoie quand il ne sait pas</small></span><input className="erp-field" placeholder="/contact" {...champ('lien_contact')} /></label>
            <label className="as-mod__champ as-mod__champ--large"><span>Message d'accueil <small>le site peut le lire</small></span><input className="erp-field" {...champ('accueil')} />{refus?.champ === 'accueil' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
            <label className="as-mod__champ"><span>Suggestions affichées <small>une par ligne, six au plus</small></span><textarea className="erp-field" rows={4} placeholder={'Apprentissage CFC\nStage découverte\nOffres d\'emploi'} {...champ('suggestions')} />{refus?.champ === 'suggestions' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
            <label className="as-mod__champ"><span>Consignes <small>son rôle, son ton</small></span><textarea className="erp-field" rows={4} {...champ('consignes')} />{refus?.champ === 'consignes' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
            <label className="as-mod__champ as-mod__champ--large">
              <span>Ce que l'assistant sait <small>la seule source de ses réponses · {String(form.connaissances || '').length.toLocaleString('fr-CH')} / 30 000 caractères</small></span>
              <textarea className="erp-field as-mod__connaissances" placeholder={'Apprentissage CFC informaticien·ne : 4 ans, Genève, rentrée en août. Salaire la première année : …\nStage découverte : 3 jours, sur demande, dès 15 ans.\nPour postuler : https://…/postuler\nOffres ouvertes : …'} {...champ('connaissances')} />
              {refus?.champ === 'connaissances' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}
              <p className="as-mod__aide">Écrivez en phrases simples ce qu'un visiteur peut demander : conditions, durées, dates, salaires, comment postuler, qui contacter. Ce qui n'est pas ici, l'assistant ne l'invente pas : il renvoie vers votre formulaire de contact. Les liens s'écrivent en entier (https://…).</p>
            </label>
            <label className="as-mod__champ"><span>Texte de repli <small>quand il ne peut pas répondre</small></span><textarea className="erp-field" rows={3} {...champ('repli')} /><p className="as-mod__aide"><code>{'{contact}'}</code> est remplacé par le lien du formulaire.</p>{refus?.champ === 'repli' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
            <label className="as-mod__champ"><span>Plafond de messages par jour <small>10 à 5 000</small></span><input className="erp-field" type="number" min={10} max={5000} {...champ('plafond_jour')} /><p className="as-mod__aide">Au-delà, le site reçoit le texte de repli jusqu'au lendemain. Chaque message est une réponse de l'IA.</p>{refus?.champ === 'plafond_jour' && <p className="as-mod__erreur" role="alert">{refus.message}</p>}</label>
          </div>
          <div className="as-mod__actions">
            <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={!!occupe}><BI icon={occupe === 'enregistrer' ? 'arrow-repeat' : 'check-lg'} className={occupe === 'enregistrer' ? 'as-mod__tourne' : undefined} /> Enregistrer</button>
            <span className="as-mod__muet">Enregistrez avant d'essayer : l'essai répond avec ce qui est enregistré.</span>
          </div>
        </section>

        {/* ── 3. Essayer ── */}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre"><BI icon="send" /> Essayer, comme un visiteur</h3>
          <p className="as-mod__muet">Le même moteur que le site. Ces échanges ne comptent pas dans le plafond du jour, et sont marqués « essai » dans le journal.</p>
          {chat.length > 0 && (
            <div className="as-mod__chat" ref={chatRef}>
              {chat.map((m, i) => (
                <div key={i} className={`as-mod__bulle as-mod__bulle--${m.role}${m.issue && m.issue !== 'ok' ? ' as-mod__bulle--repli' : ''}`} title={m.issue && ISSUES[m.issue] ? ISSUES[m.issue] : undefined}>{m.text}</div>
              ))}
              {occupe === 'essai' && <div className="as-mod__bulle as-mod__bulle--bot"><BI icon="arrow-repeat" className="as-mod__tourne" /> …</div>}
            </div>
          )}
          {chat.length > 0 && chat[chat.length - 1].issue && chat[chat.length - 1].issue !== 'ok' && <p className="as-mod__alerte">{ISSUES[chat[chat.length - 1].issue]}</p>}
          <div className="as-mod__saisie">
            <input className="erp-field" placeholder="Posez une question, comme un visiteur…" value={question} onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') essayer(); }} disabled={!!occupe} maxLength={1000} />
            <button type="button" className="erp-btn erp-btn--primary" onClick={() => essayer()} disabled={!!occupe || !question.trim()}><BI icon="send" /> Envoyer</button>
            {chat.length > 0 && <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setChat([])} disabled={!!occupe}>Effacer</button>}
          </div>
          {(assistant.suggestions || []).length > 0 && chat.length === 0 && (
            <div className="as-mod__actions">
              {assistant.suggestions.map(s => <button key={s} type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => essayer(s)} disabled={!!occupe}>{s}</button>)}
            </div>
          )}
        </section>

        {/* ── 4. Ce que les visiteurs demandent ── */}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre"><BI icon="list-ul" /> Ce que les visiteurs demandent</h3>
          <div className="as-mod__stats">
            <div className="as-mod__stat"><b>{stats.aujourd_hui ?? '—'}</b><span>messages aujourd'hui · plafond {assistant.plafond_jour}</span></div>
            <div className="as-mod__stat"><b>{stats.sept_jours ?? '—'}</b><span>sur sept jours</span></div>
            <div className="as-mod__stat"><b>{stats.sept_jours_sans_reponse ?? '—'}</b><span>restés sans réponse (plafond, refus, indisponible)</span></div>
          </div>
          {journal == null ? <p className="as-mod__muet">Chargement…</p>
            : journal.length === 0 ? <p className="as-mod__muet">Aucun échange pour l'instant. Dès que le site est branché, les questions des visiteurs apparaissent ici — c'est là qu'on voit ce qui manque dans les informations.</p>
            : (
              <ul className="as-mod__journal">
                {journal.map(e => (
                  <li key={e.id}>
                    <span className="as-mod__quand">{quand(e.created_at)}</span>
                    <span className="as-mod__q">{e.question}{e.essai && <span className="as-mod__essai">essai</span>}</span>
                    {e.reponse ? <span className="as-mod__r">{e.reponse}</span> : <span className="as-mod__issue">{ISSUES[e.issue] || 'Sans réponse.'}</span>}
                  </li>
                ))}
              </ul>
            )}
        </section>

        {/* ── 5. La clé ── */}
        <section className="as-mod__bloc erp-card">
          <h3 className="as-mod__titre"><BI icon="key" /> La clé</h3>
          <p className="as-mod__texte">L'adresse porte une clé propre à cet assistant. Elle est visible dans le code de votre site — c'est normal : ce sont les sites autorisés et le plafond qui bornent son usage. Si elle a fuité ou que vous changez de site, renouvelez-la : l'ancienne adresse meurt sur-le-champ.</p>
          <div className="as-mod__actions">
            <button type="button" className="erp-btn erp-btn--secondary" onClick={renouveler} disabled={!!occupe}><BI icon="arrow-clockwise" /> Renouveler la clé</button>
            <button type="button" className="erp-btn erp-btn--danger" onClick={supprimer} disabled={!!occupe}><BI icon="trash" /> Supprimer cet assistant</button>
          </div>
        </section>
      </>)}
    </div>
  );
}
